<template>
  <!-- Компонент вопрос -->
  <QuizQuestion :question="question" />
  <!-- Компонент вопрос -->

  <div class="quiz-options quiz__fill-in-blanks" :class="{ 'is-choosing': activeBlankIdx !== null || activeWord !== null }">
    <div class="quiz__blank-text">
      <template v-for="(part, idx) in blanksLayout" :key="'part-' + idx">
        <template v-if="typeof part === 'string'">{{ part }}</template>
        <div
          v-else
          class="blank"
          :class="{
            filled: userBlanks[part.blankIdx],
            empty: !userBlanks[part.blankIdx],
            chosen: userBlanks[part.blankIdx],
            active: activeBlankIdx === part.blankIdx
          }"
          @click="selectBlank(part.blankIdx)"
        >
          <span v-if="userBlanks[part.blankIdx]">{{ userBlanks[part.blankIdx] }}</span>
          <span v-else class="placeholder">...</span>
          <button v-if="userBlanks[part.blankIdx]" type="button" @click.stop="removeFromBlank(part.blankIdx)">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </template>
    </div>
    <div class="quiz__blank-answers">
      <div
        v-for="(word, idx) in availableWords"
        :key="'word-' + idx"
        class="answer"
        :class="{
          chosen: !isWordAvailable(word),
          active: activeWord === word
        }"
        @click="selectWord(word)"
      >
        {{ word }}
      </div>
    </div>
  </div>
</template>

<script>
import QuizQuestion from "@/components/QuizQuestion/QuizQuestion.vue";

export default {
  name: 'FillInTheBlanksQuestion',
  components: {
    QuizQuestion
  },
  props: {
    question: {
      type: Object,
      required: true
    },
    selectedAnswers: {
      type: Array,
      default: () => []
    },
    showResults: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userBlanks: [], // массив выбранных слов по индексам пропусков
      activeBlankIdx: null, // индекс активного пропуска
      activeWord: null, // активное выбранное слово
    };
  },
  computed: {
    blanksLayout() {
      // Собираем массив: текст/пропуск/текст/пропуск...
      const layout = [];
      let blankIdx = 0;
      for (let i = 0; i < this.question.text_template.length; i++) {
        if (this.question.text_template[i] === null) {
          layout.push({ blankIdx });
          blankIdx++;
        } else {
          layout.push(this.question.text_template[i]);
        }
      }
      return layout;
    },
    availableWords() {
      return this.question.blank_options || [];
    },
    isAllFilled() {
      return this.userBlanks.length === this.numBlanks && this.userBlanks.every(w => !!w);
    },
    numBlanks() {
      return this.question.text_template.filter(x => x === null).length;
    }
  },
  watch: {
    selectedAnswers: {
      immediate: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length === this.numBlanks) {
          this.userBlanks = [...newVal];
        } else {
          this.userBlanks = Array(this.numBlanks).fill("");
        }
      }
    },
    question: {
      immediate: true,
      handler() {
        this.userBlanks = Array(this.numBlanks).fill("");
        this.activeBlankIdx = null;
        this.activeWord = null;
      }
    }
  },
  methods: {
    isWordAvailable(word) {
      // Слово доступно, если оно не выбрано ни в одном пропуске
      return !this.userBlanks.includes(word);
    },
    selectBlank(blankIdx) {
      // Если выбрано слово, вставляем его в пропуск
      if (this.activeWord !== null && this.isWordAvailable(this.activeWord)) {
        this.userBlanks[blankIdx] = this.activeWord;
        this.activeWord = null;
        this.activeBlankIdx = null;
        this.emitAnswers();
      } else {
        // Иначе просто выбираем пропуск для заполнения
        this.activeBlankIdx = blankIdx;
        this.activeWord = null;
      }
    },
    selectWord(word) {
      // Если не доступно, игнорируем
      if (!this.isWordAvailable(word)) return;

      // Если выбран пропуск, вставляем слово
      if (this.activeBlankIdx !== null) {
        this.userBlanks[this.activeBlankIdx] = word;
        this.activeBlankIdx = null;
        this.activeWord = null;
        this.emitAnswers();
      } else {
        // Иначе просто выбираем слово
        this.activeWord = word;
        this.activeBlankIdx = null;
      }
    },
    addToBlank(word) {
      // Этот метод теперь используется только для совместимости
      this.selectWord(word);
    },
    removeFromBlank(blankIdx) {
      if (this.userBlanks[blankIdx]) {
        this.userBlanks[blankIdx] = "";
        this.activeBlankIdx = null;
        this.activeWord = null;
        this.emitAnswers();
      }
    },
    emitAnswers() {
      // Эмитим массив выбранных слов
      this.$emit('answers-updated', this.question.id, [...this.userBlanks]);
    }
  }
};
</script>

<style lang="scss">
.quiz-options.quiz__fill-in-blanks {
  .quiz__blank-text {
    display: inline-block;
    margin-bottom: 49px;
    color: var(--dark-gray, #585858);
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;

    & .blank {
      position: relative;
      display: inline-flex;
      height: 34px;
      margin: 8px;
      padding: 4px 10px 6px 10px;
      align-items: center;
      gap: 10px;
      flex-shrink: 0;
      min-width: 156px;
      width: auto;
      align-self: stretch;
      color: var(--dark-gray, #585858);
      border-radius: 36px;
      border: 2px dashed var(--gray, #AAA);
      box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.05);
      cursor: pointer;
      transition: $duration $timing-function;

      & span {
        display: flex;
        height: 30px;
        min-width: 136px;
        align-items: center;
        
        &.placeholder {
          color: var(--gray, #AAA);
          font-weight: 400;
          opacity: 0;
        }
      }

      &.chosen {
        border: 2px solid var(--white, #FFF);
        background: var(--white, #FFF);
      }

      & button {
        position: absolute;
        top: 50%;
        right: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        transform: translateY(-50%);

        & svg {
          color: var(--light-gray);
        }
      }
    }
  }

  .quiz__blank-answers {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    & .answer {
      display: flex;
      padding: 4px 10px 6px 10px;
      align-items: center;
      gap: 10px;
      color: var(--dark-gray, #585858);
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      border-radius: 36px;
      border: 2px solid var(--white, #FFF);
      background: var(--white, #FFF);
      box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.05);
      cursor: pointer;
      transition: $duration $timing-function;

      &.chosen {
        opacity: 0.4;
      }
    }
  }

  //Выбираем ответы
  &.is-choosing {
    & .quiz__blank-text .blank.active {
      border-color: var(--red);
    }

    & .quiz__blank-answers {
      & .answer {
        box-shadow: 0px 5px 16px 0px rgba(236, 0, 26, 0.50);
      }
    }
  }

  //ответы выбраны

}
</style>
